import {
  createContext,
  useEffect,
  useState,
  useContext,
  ReactNode,
} from "react";
import { supabase } from "../lib/supabase";
import type { Session } from "@supabase/supabase-js";
import type { SignUpData } from "../types/auth";
import { TERMS_VERSION } from "../constants/terms";

const AuthContext = createContext<any>(undefined);

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);

    const signUp = async (d: SignUpData) => {
        // Capture IP address client-side
        let ipAddress: string | null = null;
        try {
            const ipResponse = await fetch('https://api.ipify.org?format=json');
            const ipData = await ipResponse.json();
            ipAddress = ipData.ip;
        } catch (err) {
            console.warn('Failed to fetch IP address:', err);
            // Continue without IP if fetch fails
        }

        // Prepare terms acceptance data
        const userAcceptedTermsAt = new Date().toISOString();

        const { data, error } = await supabase.auth.signUp({
            email: d.email,
            password: d.password1,
            options: {
                data: {
                    first_name: d.first,
                    last_name: d.last,
                    role: d.role,
                    user_accepted_terms_at: userAcceptedTermsAt,
                    terms_acceptance_ip: ipAddress,
                    terms_version: TERMS_VERSION,
                    affordability_budget_min: d.budgetMin ?? null,
                    affordability_budget_max: d.budgetMax ?? null,
                },
            },
        });

    if (error) {
      // console.error("Problem signing up:", error)
      return { success: false, error };
    }

    // Best-effort: persist affordability to students.generator_answers for students.
    // This will succeed only when the new user is signed in (session present).
    try {
      if (
        d.role === "student" &&
        data?.user?.id &&
        (d.budgetMin != null || d.budgetMax != null)
      ) {
        await supabase
          .from("students")
          .update({
            generator_answers: {
              "6": { min: d.budgetMin ?? 0, max: d.budgetMax ?? 100000 },
              "21": false,
            },
          })
          .eq("auth_user_id", data.user.id);
      }
    } catch {
      // ignore
    }
    return { success: true, data };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      // console.error("Problem signing in:", error)
      return { success: false, error };
    }
    console.log("Sign in success:", data);
    return { success: true, data };
  };

  const signOut = async () => {
    // Clear React state first so LandingRedirect sees null immediately
    setSession(null)

    // Fire-and-forget Supabase signOut (don't await)
    supabase.auth.signOut().catch(() => {})

    // Clear sessionStorage for this tab
    Object.keys(sessionStorage)
      .filter(k => k.startsWith('sb-') || k.includes('supabase'))
      .forEach(k => sessionStorage.removeItem(k))

    // Clear any lingering localStorage keys
    Object.keys(localStorage)
      .filter(k => k.startsWith('sb-') || k.includes('supabase'))
      .forEach(k => localStorage.removeItem(k))

    // Broadcast sign-out to other open tabs
    if ('BroadcastChannel' in window) {
      const ch = new BroadcastChannel('triptic-auth-sync')
      ch.postMessage({ type: 'signout' })
      ch.close()
    }

    window.location.replace('/')
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' && session === null) {
        setSession(prev => {
          if (!prev) return prev

          // The SDK fires the same SIGNED_OUT event for a real sign-out and
          // for an internal token refresh that the server rejected (e.g. a
          // JWT-rejection incident) — there's no way to tell them apart from
          // the event alone. Since we already had a session, give it one
          // chance to recover before tearing down an already-rendered UI.
          supabase.auth.refreshSession().then(({ data, error }) => {
            if (!error && data.session) {
              setSession(data.session)
            } else {
              setSession(null)
            }
          })

          return prev
        })
        return
      }

      setSession(prev => {
        if (
          prev?.user?.id === session?.user?.id &&
          prev?.access_token === session?.access_token
        ) {
          return prev
        }
        return session
      })
    });

    // Listen for sign-out broadcast from other tabs
    let ch: BroadcastChannel | null = null
    if ('BroadcastChannel' in window) {
      ch = new BroadcastChannel('triptic-auth-sync')
      ch.onmessage = (e) => {
        if (e.data?.type === 'signout') {
          setSession(null)
          Object.keys(sessionStorage)
            .filter(k => k.startsWith('sb-'))
            .forEach(k => sessionStorage.removeItem(k))
          window.location.replace('/')
        }
      }
    }

    return () => {
      ch?.close()
    }
  }, []);

  return (
    <AuthContext.Provider value={{ session, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const UserAuth = () => {
  return useContext(AuthContext);
};
